import { useCallback, useEffect, useRef } from 'react';
import {
  type PreviewGenerationStatus,
  type PreviewInfo,
  useGeneratePreviewPreviewGeneratePostMutation,
} from '@shared/api';
import { socket } from '@shared/services/socket';
import { normalizePreviewUrl } from '../lib/preview-url';

interface UsePreviewGenerationOptions {
  organizationId?: number;
  onGenerated: (preview: PreviewInfo) => void;
}

export const usePreviewGeneration = ({
  organizationId,
  onGenerated,
}: UsePreviewGenerationOptions) => {
  const [generateRequest, { isLoading: isGenerating }] =
    useGeneratePreviewPreviewGeneratePostMutation();
  const pendingUrlsRef = useRef(new Set<string>());
  const onGeneratedRef = useRef(onGenerated);

  useEffect(() => {
    onGeneratedRef.current = onGenerated;
  }, [onGenerated]);

  useEffect(() => {
    const unsubscribe = socket.onMessage('PREVIEW_GENERATED', (preview) => {
      if (!pendingUrlsRef.current.delete(preview.url)) return;

      onGeneratedRef.current(preview);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    pendingUrlsRef.current.clear();
  }, [organizationId]);

  const generatePreview = useCallback(
    async (url: string): Promise<PreviewGenerationStatus> => {
      if (!organizationId) throw new Error('Organization is not selected');

      const normalizedUrl = normalizePreviewUrl(url);
      if (!normalizedUrl) throw new Error('Invalid preview URL');

      const response = await generateRequest({
        previewGenerateData: {
          organization_id: organizationId,
          url: normalizedUrl,
        },
      }).unwrap();

      if (response.status === 'generating') pendingUrlsRef.current.add(normalizedUrl);

      return response.status;
    },
    [generateRequest, organizationId],
  );

  return { generatePreview, isGenerating };
};
